import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, ExternalLink, Loader2, Package, QrCode, RefreshCw } from "lucide-react";
import {
  PLAN_LABELS, formatTry, type AccountPackage, type AccountPackagesSummary, type OrganizerAddonRow, type OrganizerOrderRow,
} from "@wedding/shared";
import { organizerPackagesApi } from "../lib/api";
import { paths } from "../lib/paths";
import { AppNavLink } from "../lib/route-primitives";
import { OrganizerPageHeader } from "../components/organizer-page-header";
import { cn } from "../lib/utils";

const WEB_BASE = (import.meta.env.VITE_WEB_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:5173";

type Data = { packages: AccountPackagesSummary; orders: OrganizerOrderRow[]; addons: OrganizerAddonRow[] };

function formatBytes(n: number, lang: string): string {
  const nf = new Intl.NumberFormat(lang, { maximumFractionDigits: 1 });
  return n >= 1024 ** 3 ? `${nf.format(n / 1024 ** 3)} GB` : `${nf.format(n / 1024 ** 2)} MB`;
}

/**
 * Paketlerim (2026-10-16, paket modeli Faz 1). Her düğün bir pakete bağlıdır:
 * hesabın 1 Free paketi + her ödenmiş sipariş bir paket (+ eski düğünlerin geçiş paketleri).
 * Paketler: kullanılabilir (yeni düğün açar) / kullanımda / ödeme bekleyen / biten.
 * Siparişler: tam geçmiş (iptaller dahil) + QR ekstra kişi paketleri.
 * Satın alma Faz 2'de uygulama içine gelir; şimdilik tanıtım sitesindeki paketler.
 */
export function PackagesPage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<Data | null>(null);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState<"packages" | "orders">("packages");

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const res = await organizerPackagesApi.list();
      if (res.data) setData(res.data);
      else setFailed(true);
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const date = (s: string | null) =>
    s ? new Date(`${s.replace(" ", "T")}${s.includes("T") ? "" : "Z"}`).toLocaleDateString(i18n.language, { dateStyle: "medium" }) : "";
  const limitsText = (p: AccountPackage) =>
    t("packages.limits", {
      guests: p.limits.guests,
      media: new Intl.NumberFormat(i18n.language).format(p.limits.media),
      storage: formatBytes(p.limits.storageBytes, i18n.language),
    });
  const pkgName = (p: AccountPackage) =>
    p.source === "legacy" ? t("packages.legacyName", { plan: p.label }) : p.source === "free" ? t("packages.freeName") : p.label;

  const summary = data?.packages;
  const tiles = summary
    ? [
        { key: "available", n: summary.available.length, label: t("packages.tileAvailable") },
        { key: "inUse", n: summary.inUse.length, label: t("packages.tileInUse") },
        { key: "pending", n: summary.pending.length, label: t("packages.tilePending") },
      ]
    : [];

  const PackageRow = ({ p }: { p: AccountPackage }) => (
    <li className="flex flex-col gap-2 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center" data-testid={`package-${p.id}`}>
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Package className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span className="font-semibold">{pkgName(p)}</span>
          {p.number && <span className="text-xs text-muted-foreground tabular-nums">{p.number}</span>}
          {p.amountTry !== null && p.source === "order" && (
            <span className="text-xs text-muted-foreground tabular-nums">{formatTry(p.amountTry, i18n.language)}</span>
          )}
        </p>
        <p className="text-xs text-muted-foreground">{limitsText(p)}</p>
        {p.status === "available" && p.source === "free" && <p className="text-xs text-muted-foreground">{t("packages.freeHint")}</p>}
        {p.status === "available" && p.source === "order" && <p className="text-xs text-muted-foreground">{t("packages.paidHint")}</p>}
        {p.status === "pending" && (
          <p className="text-xs text-amber-700 dark:text-amber-400">{t("packages.pendingHint", { date: date(p.createdAt) })}</p>
        )}
        {p.status === "ended" && (
          <p className="text-xs text-muted-foreground">
            {t(`packages.ended.${p.endedReason ?? "cancelled"}`)}
            {p.wedding ? ` · ${p.wedding.title}` : ""}
          </p>
        )}
      </div>
      {p.status === "available" && (
        <AppNavLink
          to={paths.newWeddingWith(p.id)}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary/90 cursor-pointer"
        >
          <span>{t("packages.openWedding")}</span>
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </AppNavLink>
      )}
      {p.status === "assigned" && p.wedding && (
        <AppNavLink
          to={paths.wedding.dashboard(p.wedding.slug)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline cursor-pointer"
        >
          <span className="truncate">{p.wedding.title}</span>
          <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
        </AppNavLink>
      )}
    </li>
  );

  const Section = ({ title, items, empty, testId }: { title: string; items: AccountPackage[]; empty?: string; testId: string }) =>
    items.length === 0 && !empty ? null : (
      <section className="space-y-2" data-testid={testId}>
        <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {title} <span className="tabular-nums">({items.length})</span>
        </h2>
        {items.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">{empty}</p>
        ) : (
          <ul className="space-y-2">
            {items.map((p) => <PackageRow key={p.id} p={p} />)}
          </ul>
        )}
      </section>
    );

  const statusPill = (status: "pending" | "paid" | "cancelled") => (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-semibold",
        status === "paid" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
          : status === "pending" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
          : "bg-muted text-muted-foreground",
      )}
    >
      {t(`profile.orderStatus.${status}`)}
    </span>
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <OrganizerPageHeader />
      <main className="mx-auto max-w-4xl space-y-6 px-4 py-8 md:px-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <h1 className="font-serif text-3xl italic md:text-4xl">{t("packages.title")}</h1>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">{t("packages.subtitle")}</p>
          </div>
          <a
            href={`${WEB_BASE}/#plans`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl border border-primary/40 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/5 cursor-pointer"
          >
            <span>{t("packages.buy")}</span>
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>

        {failed ? (
          <div className="flex flex-col items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm" role="alert">
            <p>{t("packages.loadFailed")}</p>
            <button
              type="button"
              onClick={() => void load()}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline cursor-pointer"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              <span>{t("guestCapacity.retry")}</span>
            </button>
          </div>
        ) : !data ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" aria-label={t("common.loading")} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3" data-testid="packages-summary">
              {tiles.map((tile) => (
                <div key={tile.key} className="rounded-xl border border-primary/20 bg-primary/5 p-4">
                  <p className="text-2xl font-bold tabular-nums">{tile.n}</p>
                  <p className="text-xs text-muted-foreground">{tile.label}</p>
                </div>
              ))}
            </div>

            <div role="tablist" aria-label={t("packages.title")} className="flex gap-1 border-b border-border">
              {(["packages", "orders"] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  onClick={() => setTab(key)}
                  className={cn(
                    "-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors cursor-pointer",
                    tab === key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  {key === "packages" ? t("packages.tabPackages") : t("packages.tabOrders")}
                </button>
              ))}
            </div>

            {tab === "packages" ? (
              <div className="space-y-6" role="tabpanel">
                <Section
                  title={t("packages.available")}
                  items={summary!.available}
                  empty={t("packages.noneAvailable")}
                  testId="packages-available"
                />
                <Section title={t("packages.inUse")} items={summary!.inUse} testId="packages-in-use" />
                <Section title={t("packages.pending")} items={summary!.pending} testId="packages-pending" />
                <Section title={t("packages.endedTitle")} items={summary!.ended} testId="packages-ended" />
              </div>
            ) : (
              <div className="space-y-6" role="tabpanel">
                <section className="space-y-2">
                  <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t("profile.myOrders")}</h2>
                  {data.orders.length === 0 ? (
                    <p className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">{t("profile.noOrders")}</p>
                  ) : (
                    <ul className="divide-y divide-border rounded-xl border border-border" data-testid="packages-orders">
                      {data.orders.map((o) => (
                        <li key={o.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-sm">
                          <span className="font-medium">
                            {o.ent_max_guests ? t("profile.orderPkg", { plan: o.package_label ?? PLAN_LABELS[o.plan], n: o.ent_max_guests }) : (o.package_label ?? PLAN_LABELS[o.plan])}
                          </span>
                          <span className="text-xs text-muted-foreground tabular-nums">{o.number}</span>
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {o.legacy ? t("profile.orderLegacy") : formatTry(o.amount_try, i18n.language)}
                          </span>
                          <span className="ml-auto">{statusPill(o.status)}</span>
                          <span className="w-full text-xs text-muted-foreground">
                            {date(o.created_at)}
                            {" · "}
                            {o.status === "paid"
                              ? o.wedding ? t("profile.orderWedding", { title: o.wedding.title }) : t("packages.orderUnassigned")
                              : o.status === "pending" ? t("packages.orderPendingShort") : t("profile.orderCancelled")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                {data.addons.length > 0 && (
                  <section className="space-y-2">
                    <h2 className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      <QrCode className="h-3.5 w-3.5" aria-hidden="true" />
                      <span>{t("packages.addonsTitle")}</span>
                    </h2>
                    <ul className="divide-y divide-border rounded-xl border border-border" data-testid="packages-addons">
                      {data.addons.map((a) => (
                        <li key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-sm">
                          <span className="font-medium">{t("packages.addonSeats", { seats: a.seats })}</span>
                          <span className="text-xs text-muted-foreground tabular-nums">{formatTry(a.amount_try, i18n.language)}</span>
                          <span className="ml-auto">{statusPill(a.status)}</span>
                          <span className="w-full text-xs text-muted-foreground">
                            {date(a.created_at)} · {a.wedding.title}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
